/**
 * @file User / material analysis UI — pages/Dashboard/User/MaterialAnalysisInput/components/MaterialAnalysisPdfReport.tsx.
 */

import React, { useMemo } from "react";
import type { FormValues } from "../hooks/useMaterialAnalysisForm";
import type { MaterialAnalysisSubmission, EmbodiedCarbonFactorStats } from "../../../../../services/materialAnalysisService";
import { formatMaterialAnalysisSubmittedOn } from "../../../../../services/materialAnalysisService";
import { formatMaterialAnalysisGeographicalLabel } from "../../../../../utils/backgroundGeographicalSpecificity";
import { EmbodiedCarbonFactorCard } from "./EmbodiedCarbonFactorCard";
import type { DropdownOption } from "../../../../../interfaces/dropdownInterfaces";
import {
  formatReliabilityDropdownLabel,
  formatSubmissionDataReviewLabel,
} from "../utils/reliabilityFieldLabels";
import "./MaterialAnalysisPdfReport.css";

type Props = {
  formValues: FormValues;
  submissions: MaterialAnalysisSubmission[];
  tableTitle?: string;
  embodiedCarbonFactor?: EmbodiedCarbonFactorStats | null;
  dataSpecificityApplied?: boolean;
  materialName: string;
  subMaterialName?: string;
  materialPropertyLabel?: string;
  geoLabel: string;
  technologyName: string;
  manufacturerName: string;
  dataReviewOptions?: DropdownOption[];
  dataCompletenessOptions?: DropdownOption[];
  dataAcquisitionOptions?: DropdownOption[];
};

const getGeographicalLabel = (s: MaterialAnalysisSubmission): string =>
  formatMaterialAnalysisGeographicalLabel(s);

const PdfTableHeader: React.FC<{ lines: string[] }> = ({ lines }) => (
  <th>
    {lines.map((line, index) => (
      <span key={`${index}-${line}`} className="pdf-table-th__line">
        {line}
      </span>
    ))}
  </th>
);

const PDF_TABLE_COL_WIDTHS = [
  "6.5%",
  "7%",
  "8%",
  "8%",
  "6.5%",
  "7.5%",
  "7.5%",
  "8.5%",
  "9%",
  "9%",
  "9%",
  "13.5%",
] as const;

const formatCarbonCell = (s: MaterialAnalysisSubmission): string => {
  const summary = s.a1a3Summary || s.a3Summary || s.a2Summary || s.a1Summary || null;
  const raw = summary?.embodiedCarbonValue;
  if (raw === undefined || raw === null) return "-";
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n)) return "-";
  return n.toFixed(2);
};

/** Exported const — Material Analysis Pdf Report. */
export const MaterialAnalysisPdfReport: React.FC<Props> = ({
  formValues,
  submissions,
  tableTitle = "Datasets meeting input requirements",
  embodiedCarbonFactor,
  dataSpecificityApplied = false,
  materialName,
  subMaterialName,
  materialPropertyLabel,
  geoLabel,
  technologyName,
  manufacturerName,
  dataReviewOptions = [],
  dataCompletenessOptions = [],
  dataAcquisitionOptions = [],
}) => {
  const materialLabel = subMaterialName ? `${materialName} (${subMaterialName})` : materialName;

  const tableRows = useMemo(() => {
    return (submissions || []).map((s) => ({
      datasetId: s.submissionCode || s.submissionId,
      submittedOn: formatMaterialAnalysisSubmittedOn(s),
      material: s.materialName,
      subMaterial: s.subMaterialName || "-",
      dataReview: formatSubmissionDataReviewLabel(s.dataReview, dataReviewOptions),
      dataCompleteness: formatReliabilityDropdownLabel(
        dataCompletenessOptions,
        s.dataCompleteness
      ),
      dataAcquisition: formatReliabilityDropdownLabel(
        dataAcquisitionOptions,
        s.dataAcquisition
      ),
      temporalRepresentativeness: `${s.startYear ?? "-"} – ${s.endYear ?? "-"}`,
      geographical: getGeographicalLabel(s),
      technology: s.technologicalSpecificity?.name ?? "-",
      manufacturer: s.manufacturerSpecificity?.name ?? "-",
      carbon: formatCarbonCell(s),
    }));
  }, [
    submissions,
    dataReviewOptions,
    dataCompletenessOptions,
    dataAcquisitionOptions,
  ]);

  const tablePages = useMemo(() => {
    const rowsPerPage = 18;
    const pages: typeof tableRows[] = [];
    for (let i = 0; i < tableRows.length; i += rowsPerPage) {
      pages.push(tableRows.slice(i, i + rowsPerPage));
    }
    return pages.length ? pages : [[]];
  }, [tableRows]);

  return (
    <div className="pdf-report">
      {/* PAGE 1 */}
      <section className="pdf-page" data-pdf-page="1">
        <div className="pdf-banner" role="heading" aria-level={1}>
          <span className="pdf-banner__title">ICoMEC Dashboard Result</span>
        </div>

        <div className="pdf-grid">
          <div className="pdf-grid__left">
            <h2 className="pdf-section-title">Input conditions</h2>
            <div className="pdf-kv">
              <div className="pdf-kv__row">
                <div className="pdf-kv__k">Material</div>
                <div className="pdf-kv__v">{materialName || "-"}</div>
              </div>
              <div className="pdf-kv__row">
                <div className="pdf-kv__k">Material sub-type</div>
                <div className="pdf-kv__v">{subMaterialName || "-"}</div>
              </div>
              <div className="pdf-kv__row">
                <div className="pdf-kv__k">Material Properties</div>
                <div className="pdf-kv__v">{materialPropertyLabel || "-"}</div>
              </div>

              <div className="pdf-kv__row">
                <div className="pdf-kv__k">Data Review</div>
                <div className="pdf-kv__v">{formValues.dataReview || "-"}</div>
              </div>
              <div className="pdf-kv__row">
                <div className="pdf-kv__k">Data Completeness</div>
                <div className="pdf-kv__v">{formValues.dataCompleteness || "-"}</div>
              </div>
              <div className="pdf-kv__row">
                <div className="pdf-kv__k">Data Acquisition</div>
                <div className="pdf-kv__v">{formValues.dataAcquisition || "-"}</div>
              </div>

              <div className="pdf-kv__row">
                <div className="pdf-kv__k">Temporal Specificity</div>
                <div className="pdf-kv__v">
                  {formValues.temporalRange?.[0]} – {formValues.temporalRange?.[1]}
                </div>
              </div>
              <div className="pdf-kv__row">
                <div className="pdf-kv__k">Geographical Specificity</div>
                <div className="pdf-kv__v">{geoLabel || "-"}</div>
              </div>
              <div className="pdf-kv__row">
                <div className="pdf-kv__k">Technological Specificity</div>
                <div className="pdf-kv__v">{technologyName || "Agnostic"}</div>
              </div>
              <div className="pdf-kv__row">
                <div className="pdf-kv__k">Manufacturer Specificity</div>
                <div className="pdf-kv__v">{manufacturerName || "Agnostic"}</div>
              </div>
            </div>
          </div>

          <div className="pdf-grid__right">
            <EmbodiedCarbonFactorCard
              hasData={true}
              materialSelected={true}
              submissions={submissions}
              embodiedCarbonFactor={embodiedCarbonFactor}
              dataSpecificityApplied={dataSpecificityApplied}
              materialLabel={materialLabel}
              materialName={materialName}
              subMaterialName={subMaterialName}
            />
          </div>
        </div>
      </section>

      {/* TABLE PAGES */}
      {tablePages.map((rows, idx) => (
        <section className="pdf-page" data-pdf-page={`${idx + 2}`} key={idx}>
          <div className="pdf-banner" role="heading" aria-level={1}>
            <span className="pdf-banner__title">ICoMEC Dashboard Result</span>
          </div>
          <h2 className="pdf-section-title">{tableTitle}</h2>

          <table className="pdf-table" aria-label={tableTitle}>
            <colgroup>
              {PDF_TABLE_COL_WIDTHS.map((width, index) => (
                <col key={index} style={{ width }} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <PdfTableHeader lines={["Dataset ID"]} />
                <PdfTableHeader lines={["Submitted on"]} />
                <PdfTableHeader lines={["Material"]} />
                <PdfTableHeader lines={["Material", "sub-type"]} />
                <PdfTableHeader lines={["Data", "review"]} />
                <PdfTableHeader lines={["Data", "completeness"]} />
                <PdfTableHeader lines={["Data", "acquisition"]} />
                <PdfTableHeader lines={["Temporal", "representativeness"]} />
                <PdfTableHeader lines={["Geographical", "representativeness"]} />
                <PdfTableHeader lines={["Technological", "representativeness"]} />
                <PdfTableHeader lines={["Manufacturer", "representativeness"]} />
                <PdfTableHeader
                  lines={["Cradle to gate embodied", "carbon value", "[kg CO₂eq/kg]"]}
                />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={12} className="pdf-table__empty">
                    No datasets available.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.datasetId}>
                    <td>{r.datasetId}</td>
                    <td>{r.submittedOn}</td>
                    <td>{r.material}</td>
                    <td>{r.subMaterial}</td>
                    <td>{r.dataReview}</td>
                    <td>{r.dataCompleteness}</td>
                    <td>{r.dataAcquisition}</td>
                    <td>{r.temporalRepresentativeness}</td>
                    <td>{r.geographical}</td>
                    <td>{r.technology}</td>
                    <td>{r.manufacturer}</td>
                    <td>{r.carbon}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
};

